import React, { useEffect, useRef, useState } from 'react';
import { Upload, X, ExternalLink } from 'lucide-react';
import type { Order, PaymentMethodOption } from '../../types';
import { STRIPE_METHOD_KEY } from '../../types';
import {
  adminSendInvoice,
  getPaymentMethods,
  getPublicSettings,
  uploadImage,
} from '../../services/api';
import InvoiceDocument, { invoiceBusiness } from '../InvoiceDocument';

interface Props {
  order: Order;
  onClose: () => void;
  onSent: (next: Order, emailed: boolean) => void;
}

const SendInvoiceModal: React.FC<Props> = ({ order, onClose, onSent }) => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [sampleUrl, setSampleUrl] = useState<string | undefined>(order.invoice_sample_image_url);
  const [methods, setMethods] = useState<PaymentMethodOption[]>([]);
  const [business, setBusiness] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string[]>(order.accepted_payment_methods ?? []);
  const [uploading, setUploading] = useState(false);
  // Which button is working, so only that one shows its progress.
  const [busy, setBusy] = useState<'send' | 'build' | undefined>();
  const [error, setError] = useState<string | undefined>();

  // Nowhere to email it. The invoice is still worth building — its link is
  // what gets texted over instead.
  const hasEmail = Boolean(order.customer_email?.trim());

  useEffect(() => {
    getPaymentMethods()
      .then(list => {
        setMethods(list);
        // First time invoicing this order: default to every method that's
        // ready to use. A method with no details filled in (e.g. Zelle with
        // no address yet) would give the customer nothing to act on.
        setSelected(prev =>
          prev.length > 0
            ? prev
            : list.filter(m => m.key === STRIPE_METHOD_KEY || m.details).map(m => m.key)
        );
      })
      .catch(() => setMethods([]));
    // Footer details come from settings; a failure just leaves them blank.
    getPublicSettings().then(setBusiness).catch(() => setBusiness({}));
  }, []);

  const toggle = (key: string) =>
    setSelected(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));

  // Token-addressed, and only minted on first send — so there's no link to
  // show for an invoice that hasn't gone out yet.
  const customerLink = order.invoice_token
    ? `${window.location.origin}/invoice/${order.invoice_token}`
    : '';

  const onPickFile = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(undefined);
    setUploading(true);
    try {
      const { s3_url } = await uploadImage(files[0], 'product');
      setSampleUrl(s3_url);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /**
   * Build the invoice, and email it only when asked to.
   *
   * Both buttons run this. Either way the invoice gets its number, its
   * date, its customer link and — if card is selected — its Stripe link,
   * because those are what the invoice *is*. `sendEmail` decides only
   * whether a message goes out and whether the order is marked as having
   * been invoiced. Building first and sending later reuses the number
   * already allocated rather than issuing a second one.
   */
  const submit = async (sendEmail: boolean) => {
    setError(undefined);
    setBusy(sendEmail ? 'send' : 'build');
    try {
      // Send empty string to explicitly clear an existing sample image.
      const sampleImageUrl = sampleUrl ?? '';
      const updated = await adminSendInvoice(order.order_id, {
        sampleImageUrl,
        paymentMethods: selected,
        sendEmail,
      });
      onSent(updated, sendEmail);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Could not build the invoice');
    } finally {
      setBusy(undefined);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-background-dark px-4 py-3">
          <h2 className="text-lg font-semibold">Invoice preview</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-light hover:bg-background-dark rounded p-1"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-5">
          {/* Sample image controls */}
          <section className="space-y-2">
            <div className="text-sm font-medium">Sample / mockup image (optional)</div>
            <p className="text-xs text-text-light">
              Attach a photo of a similar piece or a mockup so the customer can see what they're paying for.
            </p>
            {sampleUrl ? (
              <div className="relative inline-block">
                <img
                  src={sampleUrl}
                  alt="Invoice sample"
                  className="max-h-48 rounded border border-background-dark"
                />
                <button
                  type="button"
                  onClick={() => setSampleUrl(undefined)}
                  className="absolute top-1 right-1 bg-white/90 text-red-600 rounded-full p-1"
                  aria-label="Remove sample image"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={e => onPickFile(e.target.files)}
                  disabled={uploading}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 border border-background-dark rounded px-3 py-2 text-sm hover:border-primary disabled:opacity-50"
                >
                  <Upload size={14} /> {uploading ? 'Uploading…' : 'Add sample image'}
                </button>
              </div>
            )}
          </section>

          {/* Which payment methods this invoice accepts */}
          <section className="border-t border-background-dark pt-4 space-y-2">
            <div className="text-sm font-medium">Accepted payment methods</div>
            <p className="text-xs text-text-light">
              Pick what you'll take for this invoice. Only these appear on the customer's
              invoice and in their email.
            </p>

            {methods.length === 0 ? (
              <p className="text-xs text-text-light italic">
                No payment methods loaded — configure them under Settings.
              </p>
            ) : (
              <div className="space-y-1.5">
                {methods.map(m => {
                  const isCard = m.key === STRIPE_METHOD_KEY;
                  const missingDetails = !isCard && !m.details;
                  return (
                    <label
                      key={m.key}
                      className="flex items-start gap-2 cursor-pointer text-sm border border-background-dark rounded px-3 py-2"
                    >
                      <input
                        type="checkbox"
                        checked={selected.includes(m.key)}
                        onChange={() => toggle(m.key)}
                        className="mt-0.5"
                      />
                      <span className="flex-1">
                        <span className="font-medium">{m.label}</span>
                        {m.details && (
                          <span className="text-text-light font-mono text-xs block">
                            {m.details}
                          </span>
                        )}
                        {isCard && (
                          <span className="text-xs text-text-light block">
                            Creates a Stripe payment link. Skipped automatically if Stripe
                            isn't configured on the backend.
                          </span>
                        )}
                        {missingDetails && (
                          <span className="text-xs text-amber-700 block">
                            No details set — add them in Settings or the customer won't know
                            where to send payment.
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {selected.length === 0 && (
              <p className="text-xs text-amber-700">
                Nothing selected — the invoice will tell the customer to reply to arrange payment.
              </p>
            )}
          </section>

          {/* Exactly the document the customer receives, rendered from the
              same component their invoice page uses. */}
          <section className="border-t border-background-dark pt-4 space-y-3">
            <div className="flex items-baseline justify-between">
              <div className="text-sm font-medium">Customer preview</div>
              {!order.invoice_number && (
                <div className="text-xs text-text-light">
                  Invoice number is assigned when you send
                </div>
              )}
            </div>

            {/* Scaled down so a letter-width document fits the modal. On a
                phone the document is already laying itself out narrow, so
                shrinking it further would make it unreadable. */}
            <div className="bg-background/40 border border-background-dark rounded p-3 overflow-hidden">
              <div className="sm:origin-top-left sm:scale-[0.62] sm:w-[161%]">
                <InvoiceDocument
                  order={{ ...order, accepted_payment_methods: selected }}
                  paymentMethods={methods}
                  sampleImageUrl={sampleUrl}
                  business={invoiceBusiness(business)}
                />
              </div>
            </div>

            {customerLink ? (
              <div className="text-xs text-text-light break-all">
                Customer link:{' '}
                <a
                  href={customerLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary inline-flex items-center gap-1 hover:underline"
                >
                  {customerLink} <ExternalLink size={10} />
                </a>
              </div>
            ) : (
              <div className="text-xs text-text-light">
                The customer link is created by either button below.
              </div>
            )}
          </section>

          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>

        <div className="border-t border-background-dark px-4 py-3 space-y-2">
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="border border-background-dark rounded px-3 py-2 text-sm"
            >
              Cancel
            </button>
            {/* Two ways to finish, neither of them the lesser one: the
                link is what gets texted, the email is what gets emailed.
                Both build the same invoice, so both are primary. */}
            <button
              type="button"
              onClick={() => submit(false)}
              disabled={Boolean(busy) || uploading}
              className="bg-primary text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {busy === 'build' ? 'Creating…' : 'Create link only'}
            </button>
            <button
              type="button"
              onClick={() => submit(true)}
              disabled={Boolean(busy) || uploading || !hasEmail}
              title={hasEmail ? undefined : 'No email address on this order'}
              className="bg-primary text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {busy === 'send' ? 'Sending…' : 'Email invoice'}
            </button>
          </div>
          <p className="text-xs text-text-light text-right">
            {hasEmail
              ? 'Both create the invoice and its link. Only the second one emails it.'
              : 'No email address on this order — create the link and text it over.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SendInvoiceModal;
